import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { AlertController, ToastController } from '@ionic/angular/standalone';
import { ConnectionService, ConnectionStatus } from '../services/connection.service';
import { environment } from 'src/environments/environment';
import { Util } from '../helpers/util';
import { OfflineService } from './../services/offline.service';
import * as _ from 'lodash-es';
import { Router } from '@angular/router';
import { Operation } from '../models/operation';
import { Storage } from '@ionic/storage-angular';
import { WorkOrderTask } from './../interfaces/work-order-task';
import { IGNORED_HTTP_STATUSES } from './context/http-status.context';

/**
 * Interceptor for error
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private offlineIds: Map<string, string> = new Map();

  /**
   * Constructor of the class ErrorInterceptor
   *
   * @param authService
   * @param alertController
   * @param router
   * @param connectionService
   * @param httpClient
   * @param offlineService
   * @param storage
   */
  constructor(
    private authService: AuthService,
    public alertController: AlertController,
    private router: Router,
    public toastController: ToastController,
    private connectionService: ConnectionService,
    private httpClient: HttpClient,
    private offlineService: OfflineService,
    private storage: Storage,
  ) {}

  /**
   * Function to intercept the request
   *
   * @param request
   * @param next
   * @returns Intercepted request
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.connectionService.reachesServer()) {
      return next.handle(request).pipe(
        tap((result) => {
          if (result instanceof HttpResponse) {
            // The API answered, so we are genuinely talking to the server: record it so the
            // servers-status panel can show when the last successful communication happened.
            this.connectionService.markContact();
          }
          // If successful, make modifications in offlineData
          if (result instanceof HttpResponse && result.body) {
            const url: URL = new URL(request.url);
            const method: string = request.method;
            if (method === 'POST') {
              // Generate a id
              request.body.id = result.body.id;
            }
            const body = _.cloneDeep(request.body); // Clone to avoid modifying original body
            const parts = url.pathname.match(/(^\/users\-permissions\/roles$)|[a-zA-Z0-9_]+/g);
            let collection = parts[0];
            if (collection === '/users-permissions/roles') {
              collection = 'roles';
            }
            let id = '';
            if (parts.length > 1) {
              id = parts[1];
            }
            // If successful, make modifications in offlineData
            if (method === 'GET') {
            } else if (method === 'POST') {
              // Call offlineService.create
              this.offlineService.create(collection, body);
            } else if (method === 'PUT') {
              // Call offlineService.update
              this.offlineService.update(collection, body, id);
            } else if (method === 'DELETE') {
              // Call offlineService.delete
              this.offlineService.delete(collection, id);
            } else {
              console.error('Method not implemented', method);
            }
          }
        }),
        catchError((err) => {
          // Check if the error status is in the ignored statuses (HTTP context)
          const ignoredStatuses = request.context.get(IGNORED_HTTP_STATUSES);
          const isIgnored = ignoredStatuses.includes(err.status);
          if (isIgnored) {
            // If the status must be ignored, return the error directly.
            return throwError(() => err);
          }

          // console.error('error interceptor', err);
          if (err.status === 401) {
            // TODO: auto logout if 401 response returned from api
            this.authService.logout();
            this.router.navigate([environment.CUSTOMIZATION.PATHLOGIN]);
            return throwError(err) as Observable<any>;
          } else if (err.status === 403) {
            if (this.authService.user !== null) {
              this.handleButtonClick($localize`:@@notAllowed: No tiene permisos para realizar esta acción`, err.status, err);
            }
            // Always return a stream (logged in or not): a catchError selector that returns
            // undefined makes RxJS 7 throw "You provided 'undefined' where a stream was expected"
            // instead of surfacing the real 403 to the caller.
            return throwError(() => err) as Observable<any>;
          } else if (err.status === 503) {
            // Shard health: a site is temporarily unavailable/read-only. This is an informative
            // degradation, not an app error. The ShardHealthInterceptor already recorded it and
            // (for GET) retried; here we just let it propagate so callers can react (e.g. show a
            // manual "Reintentar" toast on writes) without the generic error toast below.
            return throwError(() => err) as Observable<any>;
          } else if (err.status !== 400) {
            // this.handleButtonClick(err);
            if (err.status === 0 || err.status === 504) {
              // Set status as disconnected
              this.connectionService.disconnect();
              // Handle request offline
              return this.handleOfflineRequest(request, next);
            } else if (err.status) {
              this.handleButtonClick($localize`:@@requestError: Ha ocurrido un error en la aplicación`, err.status, err);
            }
            // Same as the 403 branch: never let this arm fall through to `undefined` (RxJS 7 throws).
            return throwError(() => err) as Observable<any>;
          } else if (err.error || err.statusText) {
            const error = err.error.message || err.statusText;
            return throwError(error) as Observable<any>;
          } else {
            return throwError(err) as Observable<any>;
          }
        }),
      );
    } else if (request.url === environment.apiUrl + '/statuses') {
      // Just continue with the connection test request
      return next.handle(request);
    } else if (this.connectionService.getStatus() === ConnectionStatus.Offline) {
      if (!this.connectionService.checkingStatus) {
        // Send async connection test
        this.connectionService.checkingStatus = true;
        this.httpClient.get(environment.apiUrl + '/statuses').subscribe(
          async (res) => {
            this.connectionService.checkingStatus = false;
            // Set status as connected
            this.connectionService.connect();
            // Start processing the queue
            if (this.connectionService.hasPendingRequests()) {
              const alert = await this.alertController.create({
                header: $localize`:@@offlineRequestErrorHeader:Conexión recuperada`,
                message: $localize`:@@offlineRequestErrorMsg:Se ha recuperado la conexión y hay cambios pendientes de sincronizar ¿Quiere sincronizar o descartar los cambios?`,
                backdropDismiss: false,
                buttons: [
                  {
                    text: $localize`:@@sync:Sincronizar`,
                    handler: () => {
                      const rq: HttpRequest<any> = this.connectionService.getNextRequest();
                      this.handleQueueRequest(rq, next);
                    },
                  },
                  {
                    text: $localize`:@@discard:Descartar`,
                    handler: async () => {
                      const subalert = await this.alertController.create({
                        header: $localize`:@@offlineRequestErrorHeader:Confirmación`,
                        message: $localize`:@@offlineRequestErrorMsg:¿Está seguro de que desea descartar los cambios? Esta acción no puede deshacerse.`,
                        backdropDismiss: false,
                        buttons: [
                          {
                            text: $localize`:@@no:No`,
                            handler: () => {
                              const rq: HttpRequest<any> = this.connectionService.getNextRequest();
                              this.handleQueueRequest(rq, next);
                            },
                          },
                          {
                            text: $localize`:@@yes:Sí`,
                            handler: () => {
                              this.connectionService.purgeRequests();
                            },
                          },
                        ],
                      });
                      return await subalert.present();
                    },
                  },
                ],
              });
              return await alert.present();
            }
          },
          (err) => {
            this.connectionService.checkingStatus = false;
          },
        );
      }
      return this.handleOfflineRequest(request, next);
    } else if (this.connectionService.hasPendingRequests()) {
      const rq: HttpRequest<any> = this.connectionService.getNextRequest();

      if (Util.URLEquals(rq, request)) {
        // If we are processing the first from the queue, just continue processing the queue
        return this.handleQueueRequest(rq, next);
      } else {
        // Not processing the first from the queue, return offline processing
        return this.handleOfflineRequest(request, next);
      }
    }
  }

  /**
   * Function to handle queue requests
   *
   * @param rq
   * @param next
   * @returns Handle queue request
   */
  private handleQueueRequest(rq: HttpRequest<any>, next: HttpHandler): any {
    return next.handle(rq).subscribe(
      (result) => {
        // Capture offline ID correspondence
        if (result instanceof HttpResponse && rq.body && result.body) {
          this.offlineIds.set(rq.body.id, result.body.id);
        }
      },
      async (error) => {
        console.error('error interceptor request from the queue error', error);
        const alert = await this.alertController.create({
          header: $localize`:@@offlineRequestErrorHeader:Petición pendiente fallida`,
          message: $localize`:@@offlineRequestErrorMsg:Ha fallado la petición pendiente que se ha reenviado al recuperar la conexión. ¿Quiere omitirla o reintentarla?`,
          backdropDismiss: false,
          buttons: [
            {
              text: $localize`:@@ommit:Omitir`,
              handler: () => {
                return this.nextRequest(rq, next);
              },
            },
            {
              text: $localize`:@@retry:Reintentar`,
              handler: () => {
                return this.handleQueueRequest(rq, next);
              },
            },
          ],
        });
        return await alert.present();
      },
      () => {
        this.nextRequest(rq, next);
      },
    );
  }

  /**
   * Function to next request
   *
   * @param rq
   * @param next
   * @returns Next request
   */
  private nextRequest(rq, next) {
    const rq2 = this.connectionService.dequeueRequest();
    if (this.connectionService.hasPendingRequests()) {
      // If there are more pending request continue processing recursively
      let nextrq = this.connectionService.getNextRequest();
      const url: URL = new URL(nextrq.url);
      const parts = url.pathname.match(/[a-zA-Z0-9_]+/g);
      const collection = parts[0];
      // Modify the target id if it is an offline one
      if (nextrq.method === 'PUT' || nextrq.method === 'DELETE') {
        let id = '';
        if (parts.length > 1) {
          id = parts[1];
        }

        if (this.offlineIds.has(id)) {
          nextrq = nextrq.clone({
            url: nextrq.url.replace(id, this.offlineIds.get(id)),
          });
        }
      }

      if (nextrq.method === 'POST' && collection === 'upload') {
        let fd = new FormData();
        fd = nextrq.body;
        const wtaskId = fd.get('refId');
        const idTaksPhoto = this.offlineIds.get(wtaskId.toString());
        let id;
        if (wtaskId.toString().startsWith('0ffffffffff')) {
          fd.set('refId', idTaksPhoto);
          id = idTaksPhoto;
        } else {
          id = wtaskId.toString();
        }
        if (this.offlineIds.has(id)) {
          nextrq = nextrq.clone({
            url: nextrq.url.replace(id, this.offlineIds.get(id)),
          });
        }
      }

      // Modify offline ids in body
      if (nextrq.body && collection !== 'upload') {
        const bodycopy = nextrq.body;
        Object.keys(nextrq.body).forEach((key) => {
          if (this.offlineIds.has(nextrq.body[key])) {
            bodycopy[key] = this.offlineIds.get(nextrq.body[key]);
          }
        });
        nextrq = nextrq.clone({
          body: bodycopy,
        });
      }

      return this.handleQueueRequest(nextrq, next);
    }
  }

  /**
   * Function to handle offline request
   *
   * @param request
   * @param next
   * @returns Handle offline request
   */
  private handleOfflineRequest(request: HttpRequest<any>, next: HttpHandler) {
    // Identify collection and parameters
    const url: URL = new URL(request.url);
    const method: string = request.method;

    if (method === 'POST') {
      if (!request.body.id) {
        // Generate a id
        request.body.id = '0ffffffffff' + Math.random().toString(16).substring(2, 13);
      }
    }
    let body = {};
    if (typeof request.body === 'object') {
      body = _.cloneDeep(request.body); // Clone to avoid modifying original body
    }

    const parts = url.pathname.match(/(^\/users\-permissions\/roles$)|[a-zA-Z0-9_]+/g);
    let collection = parts[0];
    if (collection === '/users-permissions/roles') {
      collection = 'roles';
    }

    let id = '';
    if (parts.length > 1) {
      id = parts[1];
    }
    if (method !== 'GET' && !request.url.endsWith('/measurements/last') && (request.method === 'POST' || request.method === 'PUT' || request.method === 'DELETE')) {
      // Enqueue request to retry later
      this.connectionService.enqueueRequest(request);
    }

    let result;
    if (method === 'GET') {
      // Get parameters of the url request
      const params: Operation[] = Util.searchParamsToJS(url.searchParams, id);
      result = this.offlineService.fetch(collection, params);
    } else if (method === 'POST' && collection === 'upload') {
      this.storage.get('workorderSelected').then((workorderSelected) => {
        let fd = new FormData();
        fd = request.body;
        const wtaskId = fd.get('refId');
        const blobImage = { imgBlob: fd.get('files') };

        const pSearch = [new Operation('id', 'eq', wtaskId.toString())];
        let rworkordertask = { Photos: [] } as WorkOrderTask;
        rworkordertask = this.offlineService.fetch('workordertasks', pSearch);
        rworkordertask.Photos.push(blobImage);

        const rwtasksUpdate = this.offlineService.update('workordertasks', rworkordertask, wtaskId);

        const indexWtask = workorderSelected.workordertasks.findIndex((wt) => wt.id === wtaskId);
        workorderSelected.workordertasks[indexWtask] = rwtasksUpdate;

        const workorderUpdate = this.offlineService.update('workorders', workorderSelected, workorderSelected.id);
        result = blobImage;
      });
    } else if (method === 'POST' && collection !== 'upload') {
      // Call offlineService.create
      result = this.offlineService.create(collection, body);
    } else if (method === 'PUT' && collection !== 'workorders') {
      // Call offlineService.update
      result = this.offlineService.update(collection, body, id);
    } else if (method === 'DELETE') {
      // Call offlineService.delete
      result = this.offlineService.delete(collection, id);
    } else {
      console.error('Method not implemented', method);
    }

    // TODO: Call to service to retrieve from storage
    return of(
      new HttpResponse({
        body: result,
        status: 200,
      }),
    );
  }

  /**
   * Function to handle button click
   *
   * @param title
   * @param description
   * @param err
   */
  async handleButtonClick(title: string, description: string, err: any) {
    const toast = await this.toastController.create({
      header: title,
      message: description + ': ' + err.name,
      buttons: [
        {
          text: $localize`:@@reloadPage:Recargar página`,
          handler: () => {
            window.location.reload();
          },
        },
        {
          text: $localize`:@@continue:Continuar`,
          handler: () => {},
        },
      ],
    });
    await toast.present();
  }
}
